import React from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

interface LoaderContainerProps {
  $fullscreen?: boolean;
  $size?: 'small' | 'medium' | 'large';
  $priority?: 'high' | 'normal';
  $noPadding?: boolean;
}

const LoaderContainer = styled.div<LoaderContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-in-out;
  
  ${props => props.$fullscreen && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-background);
    z-index: ${props.$priority === 'high' ? '10000' : '9999'};
  `}
  
  ${props => !props.$fullscreen && !props.$noPadding && `
    padding: 40px 20px;
  `}
`;

interface SpinnerProps {
  $size?: 'small' | 'medium' | 'large';
}

const Spinner = styled.div<SpinnerProps>`
  border: 3px solid rgba(96, 159, 148, 0.2);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  
  ${props => {
    switch (props.$size) {
      case 'small':
        return 'width: 20px; height: 20px; border-width: 2px;';
      case 'large':
        return 'width: 60px; height: 60px; border-width: 4px;';
      default:
        return 'width: 40px; height: 40px; border-width: 3px;';
    }
  }}
`;

const LoaderText = styled.div<SpinnerProps>`
  margin-top: 16px;
  color: var(--color-text-secondary);
  font-family: var(--font-family-main);
  font-weight: 400;
  text-align: center;
  
  ${props => {
    switch (props.$size) {
      case 'small':
        return 'font-size: 12px; margin-top: 8px;';
      case 'large':
        return 'font-size: 16px; margin-top: 20px;';
      default:
        return 'font-size: 14px; margin-top: 16px;';
    }
  }}
`;

interface LoaderProps {
  text?: string;
  size?: 'small' | 'medium' | 'large';
  fullscreen?: boolean;
  showText?: boolean;
  priority?: 'high' | 'normal';
  noPadding?: boolean;
}

const Loader: React.FC<LoaderProps> = ({ 
  text = 'Загрузка...', 
  size = 'medium',
  fullscreen = false,
  showText = true,
  priority = 'normal',
  noPadding = false
}) => {
  return (
    <LoaderContainer $fullscreen={fullscreen} $size={size} $priority={priority} $noPadding={noPadding}>
      <Spinner $size={size} />
      {showText && <LoaderText $size={size}>{text}</LoaderText>}
    </LoaderContainer>
  );
};

export default Loader; 